import 'app/containers/MonitoringPage/style.scss';

import React, { PureComponent, useContext, useMemo } from 'react';
import { Form } from 'semantic-ui-react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import _, { debounce } from 'lodash';

import { DicomData } from 'app/interfaces/Dicom';
import {
  FindQueryModel,
  PACSCommunication,
  PACSError,
  Result,
} from 'app/interfaces/PACSCommunication';
import Select, { OptionProps } from 'react-select';
import { ANIMAL_SEARCH_STYLE } from 'app/components/AnimalSearch/AnimalSearchSelectStyle';
import { SearchParams } from 'app/components/PACSQuery/SearchParams';
import { getDicomValue, getPatientInfoFromDicomData } from 'app/utils/dicom/DicomData';
import { IsPACSAssociatedContext } from 'app/components/PACSCommunicationProvider';
import { PACSCommunicationContext } from '../PACSCommunicationProvider/context';
import OptionWithPatientData from 'app/components/OptionWithPatientData';
import { useSelector } from 'react-redux';
import { selectDicomMapping } from 'app/redux/userInputMapping/reducer';
import DateInput from 'app/components/Form/DateInput';

type PatientSearchParams = {
  PatientID?: string;
  PatientName?: string;
};

type SelectTextOption<T> = { value: string; label: string; data?: T };

type DicomPatient = {
  PatientName: string;
  PatientID: string;
  PatientSpeciesDescription: string;
  PatientSex: string;
  PatientSexNeutered: string;
  PatientBirthDate: string;
  PatientBreedDescription: string;
};

async function fetchPatients(
  pacsCommunication: PACSCommunication,
  searchParams: PatientSearchParams
) {
  const { PatientID, PatientName } = searchParams;

  const patientSearchResults = pacsCommunication.find(
    {
      QueryRetrieveLevel: 'PATIENT',
      PatientName: PatientName && `*${PatientName.trim()}*`,
      PatientID: PatientID && `${PatientID.trim()}*`,
    },
    FindQueryModel.Patient
  ) as Promise<DicomData[]>;

  return patientSearchResults;
}

export type PACSStudySearchProps = {
  searchParams: SearchParams;
  setSearchParams: (searchParams: (searchParams: SearchParams) => SearchParams) => void;
};

type PACSStudySearchPropsImpl = PACSStudySearchProps & {
  intl: IntlShape;
  pacsCommunication: PACSCommunication;
  isPatientLevelFindSupported: boolean;
};

function isStringEnoughForSearch(value: string) {
  return value?.length >= 3;
}

function isInvalidSopClassError(result: DicomData[] | PACSError | undefined): boolean {
  return !!result && !Array.isArray(result) && result.status === 'INVALID_SOP_CLASS';
}

function stripWildcards(value: string | undefined): string {
  if (!value) return '';
  return value.replace(/^\*+/, '').replace(/\*+$/, '');
}

type PACSStudySearchState = {
  patientIDs: SelectTextOption<DicomPatient>[];
  patientNames: SelectTextOption<DicomPatient>[];
  pendingAccessionNumber: string | undefined;
  isPatientIDSearching: boolean;
  isPatientNameSearching: boolean;
  patientLevelFindFailed: boolean;
  freeTextPatientID: string;
  freeTextPatientName: string;
};

const PatientNameOptionClass = OptionWithPatientData('name', true);

const PatientNameOption = (option: OptionProps<SelectTextOption<DicomPatient>, false>) => {
  const dicomMapping = useSelector(selectDicomMapping);
  const data = option.data;
  const formattedPatientData = getPatientInfoFromDicomData(data.data, dicomMapping);
  return <PatientNameOptionClass {...option} data={{ ...data, data: formattedPatientData }} />;
};

const PatientIDOptionClass = OptionWithPatientData('file_id', true);

const PatientIDOption = (option: OptionProps<SelectTextOption<DicomPatient>, false>) => {
  const dicomMapping = useSelector(selectDicomMapping);
  const data = option.data;

  const formattedPatientData = getPatientInfoFromDicomData(data.data, dicomMapping);

  return <PatientIDOptionClass {...option} data={{ ...data, data: formattedPatientData }} />;
};

class PACSStudySearchImpl extends PureComponent<PACSStudySearchPropsImpl, PACSStudySearchState> {
  constructor(props: PACSStudySearchPropsImpl) {
    super(props);

    this.state = {
      patientIDs: [],
      patientNames: [],
      pendingAccessionNumber: undefined,
      isPatientIDSearching: false,
      isPatientNameSearching: false,
      patientLevelFindFailed: false,
      freeTextPatientID: stripWildcards(this.props.searchParams.PatientID),
      freeTextPatientName: stripWildcards(this.props.searchParams.PatientName),
    };
  }

  noOptionsMessage = ({ inputValue }: { inputValue: string }) => {
    return inputValue.length < 3
      ? this.props.intl.formatMessage({ id: 'general.too_short' }, { nb: 3 })
      : this.props.intl.formatMessage({ id: 'pacs.query.animal_search.not_found' });
  };

  resetPatientNames = () => this.setState({ patientNames: [] });

  resetPatientIDs = () => this.setState({ patientIDs: [] });

  onFreeTextPatientIDChange = (_e: unknown, { value }: { value: string }) => {
    this.setState({ freeTextPatientID: value });
    this.props.setSearchParams((params) => ({
      ...params,
      PatientID: value ? `${value.trim()}*` : undefined,
    }));
  };

  onFreeTextPatientNameChange = (_e: unknown, { value }: { value: string }) => {
    this.setState({ freeTextPatientName: value });
    this.props.setSearchParams((params) => ({
      ...params,
      PatientName: value ? `*${value.trim()}*` : undefined,
    }));
  };

  onPatientNameChangeDebounce = debounce((value: string) => {
    fetchPatients(this.props.pacsCommunication, { PatientName: value })
      .catch(() => fetchPatients(this.props.pacsCommunication, { PatientName: value }))
      .then((patients) => {
        if (isInvalidSopClassError(patients as DicomData[] | PACSError | undefined)) {
          this.setState({ patientLevelFindFailed: true, isPatientNameSearching: false });
          return;
        }
        this.setState((state) => ({
          ...state,
          patientNames: (patients as DicomPatient[]).map((patient) => ({
            value: getDicomValue(patient.PatientName),
            label: getDicomValue(patient.PatientName),
            data: patient,
          })),
        }));
      })
      .finally(() => {
        this.setState({ isPatientNameSearching: false });
      });
  }, 500);

  onPatientNameChange = (value: string) => {
    if (value.length < 3) return;

    this.setState({ isPatientNameSearching: true });
    return this.onPatientNameChangeDebounce(value);
  };

  onFileIdChangeDebounce = debounce((value: string) => {
    fetchPatients(this.props.pacsCommunication, { PatientID: value })
      .catch(() => fetchPatients(this.props.pacsCommunication, { PatientID: value }))
      .then((patients) => {
        if (isInvalidSopClassError(patients as DicomData[] | PACSError | undefined)) {
          this.setState({ patientLevelFindFailed: true, isPatientIDSearching: false });
          return;
        }
        this.setState((state) => ({
          ...state,
          patientIDs: (patients as DicomPatient[]).map((patient) => ({
            value: getDicomValue(patient.PatientID),
            label: getDicomValue(patient.PatientID),
            data: patient,
          })),
        }));
      })
      .finally(() => {
        this.setState({ isPatientIDSearching: false });
      });
  }, 500);

  onFileIdChange = (value: string) => {
    if (value.length < 2) return;

    this.setState({ isPatientIDSearching: true });
    return this.onFileIdChangeDebounce(value);
  };

  render() {
    const { searchParams, setSearchParams, intl, isPatientLevelFindSupported } = this.props;
    const {
      patientIDs,
      patientNames,
      isPatientIDSearching,
      isPatientNameSearching,
      patientLevelFindFailed,
      freeTextPatientID,
      freeTextPatientName,
    } = this.state;
    const { onFileIdChange, onPatientNameChange } = this;
    const { PatientID, PatientName, searchStartDate, searchEndDate } = searchParams;

    const isFreeTextMode = !isPatientLevelFindSupported || patientLevelFindFailed;

    return (
      <div className="search-form">
        <label style={{ color: 'white' }}>
          <FormattedMessage id="monitoring.animalSearch" />
        </label>
        <div>
          <Form className="filters">
            <div className="animal-search">
              {isFreeTextMode ? (
                <>
                  <Form.Input
                    id="patient-id-input"
                    data-testid="patient-id-input"
                    placeholder={intl.formatMessage({ id: 'monitoring.id' })}
                    value={freeTextPatientID}
                    onChange={this.onFreeTextPatientIDChange}
                  />
                  <Form.Input
                    id="patient-name-input"
                    data-testid="patient-name-input"
                    placeholder={intl.formatMessage({ id: 'monitoring.animal_or_owner' })}
                    value={freeTextPatientName}
                    onChange={this.onFreeTextPatientNameChange}
                  />
                </>
              ) : (
                <>
                  <div className="field">
                    <Select
                      styles={ANIMAL_SEARCH_STYLE}
                      isClearable
                      isLoading={isPatientIDSearching}
                      loadingMessage={() => intl.formatMessage({ id: 'general.loading' })}
                      inputId="patient-id-input"
                      placeholder={intl.formatMessage({ id: 'monitoring.id' })}
                      onBlur={this.resetPatientIDs}
                      onInputChange={onFileIdChange}
                      onChange={(event) => {
                        const PatientID = event?.value;
                        setSearchParams((params) => ({
                          ...params,
                          PatientID,
                        }));
                      }}
                      noOptionsMessage={this.noOptionsMessage}
                      options={patientIDs}
                      value={patientIDs.find((option) => option.value === PatientID) || null}
                      components={{ Option: PatientIDOption }}
                    />
                  </div>
                  <div className="field">
                    <Select
                      styles={ANIMAL_SEARCH_STYLE}
                      isClearable
                      isLoading={isPatientNameSearching}
                      loadingMessage={() => intl.formatMessage({ id: 'general.loading' })}
                      inputId="patient-name-input"
                      placeholder={intl.formatMessage({ id: 'monitoring.animal_or_owner' })}
                      onBlur={this.resetPatientNames}
                      onInputChange={onPatientNameChange}
                      onChange={(event) => {
                        const PatientName = event?.value;
                        setSearchParams((params) => ({
                          ...params,
                          PatientName,
                        }));
                      }}
                      noOptionsMessage={this.noOptionsMessage}
                      options={patientNames}
                      value={patientNames.find((option) => option.value === PatientName) || null}
                      components={{ Option: PatientNameOption }}
                    />
                  </div>
                </>
              )}
              <DateInput
                value={searchStartDate}
                onChange={(date) =>
                  setSearchParams((params) => ({ ...params, searchStartDate: date }))
                }
                data-testid="start-date"
                label={<FormattedMessage id="date.from" />}
              />
              <DateInput
                value={searchEndDate}
                onChange={(date) =>
                  setSearchParams((params) => ({ ...params, searchEndDate: date }))
                }
                data-testid="end-date"
                label={<FormattedMessage id="date.to" />}
              />
            </div>
          </Form>
        </div>
      </div>
    );
  }
}

function isPatientLevelFindSupportedFor(associationData: unknown): boolean {
  if (associationData === undefined) return true;
  const presentationContexts = (associationData as any)?.presentation_contexts;
  if (!Array.isArray(presentationContexts)) return true;
  return _.some(
    presentationContexts,
    (ctx) => ctx.abstractSyntax === FindQueryModel.Patient && ctx.result === Result.Acceptance
  );
}

export default function PACSStudySearch(props: PACSStudySearchProps) {
  const intl = useIntl();
  const pacsCommunication = useContext(PACSCommunicationContext);
  const associationData = useContext(IsPACSAssociatedContext);
  const isPatientLevelFindSupported = useMemo(
    () => isPatientLevelFindSupportedFor(associationData),
    [associationData]
  );

  return (
    <PACSStudySearchImpl
      {...props}
      intl={intl}
      pacsCommunication={pacsCommunication}
      isPatientLevelFindSupported={isPatientLevelFindSupported}
    />
  );
}
